import { AxiosInstance } from "axios";
import { mapSeaReferenceTypeToApi, SeaQueryParams, SeaScacQueryParam } from "./query.types";
import { SeaReferenceTypesDTO } from "entities/shared/seaCard.types";
import { SearchReferenceDTO } from "entities/shared/shipmentCard.types";
import { Nullable, NullableValues, ValueOf } from "shared/lib/typeCheck.lib";

export const mutateMetadataInterceptor = (instance: AxiosInstance) => {
  // Qubictron returns the JSON body directly — unwrap AxiosResponse.
  instance.interceptors.response.use((response) => response.data);
};

type CreateSeaSearchQueryParamsParams = {
  model?: {
    reference?: string | null;
    referenceType?: ValueOf<typeof SeaReferenceTypesDTO> | string | null;
    carrierScac?: Nullable<SeaScacQueryParam>;
  };
  requestType: "openedCard" | "shippingLine" | "updateCard" | "search";
  override?: Partial<SeaQueryParams>;
};

export const createSeaSearchQueryParams = ({
  model,
  requestType,
  override,
}: CreateSeaSearchQueryParamsParams): SeaQueryParams | null => {
  const reference: Nullable<SearchReferenceDTO> = override?.reference_number ?? model?.reference;

  if (!reference) return null;

  const typeValue = override?.reference_type ?? model?.referenceType;
  const mappedType = mapSeaReferenceTypeToApi(typeValue ?? null);
  const reference_type: NullableValues<Pick<SeaQueryParams, "reference_type">> = mappedType
    ? { reference_type: mappedType }
    : {};

  const carrierScac: Nullable<SeaScacQueryParam> = override?.carrier_code ?? model?.carrierScac;
  const carrier_code: NullableValues<Pick<SeaQueryParams, "carrier_code">> = carrierScac
    ? { carrier_code: carrierScac.toUpperCase() }
    : {};

  const route_details: Nullable<boolean> = override?.route_details ?? true;

  switch (requestType) {
    case "openedCard":
      return Object.assign(
        {
          reference_number: reference,
          route_details,
        },
        carrier_code,
        reference_type
      );
    case "search": {
      return Object.assign(
        {
          reference_number: reference,
          route_details,
        },
        carrier_code,
        reference_type
      );
    }
    case "updateCard":
    case "shippingLine":
    default:
      return Object.assign({ reference_number: reference, route_details }, carrier_code, reference_type);
  }
};

export { mapSeaReferenceTypeToApi } from "./query.types";

export const generateContainersSizeTypes = (sizeTypes: string[]): string[] => {
  if (!sizeTypes?.length) return [];

  const sizeTypeValueCounts: Record<string, number> = {};

  sizeTypes.forEach((value) => {
    if (sizeTypeValueCounts[value]) {
      sizeTypeValueCounts[value]++;
    } else {
      sizeTypeValueCounts[value] = 1;
    }
  });

  const sortedSizeTypeValueCounts = Object.entries(sizeTypeValueCounts).sort((a, b) => b[1] - a[1]);

  return sortedSizeTypeValueCounts.map(([value, count]) => `${count} x ${value}`);
};
